/**
 * Package Purchase API Client
 * Created: January 17, 2026
 * 
 * API hooks and functions for package purchase with QR payment
 */

import * as React from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import {
  PackagePurchaseQRRequest,
  PackagePurchaseQRResponse,
  PackagePurchaseStatusResponse,
  PackagePurchaseHistory,
  PackageComparison
} from '@/src/types/package-purchase'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || ''

// ============================================================================
// API Functions
// ============================================================================

/**
 * Create QR code for package purchase
 */
export async function createPackagePurchaseQR(
  params: PackagePurchaseQRRequest
): Promise<PackagePurchaseQRResponse> {
  const token = localStorage.getItem('authToken')
  
  const response = await fetch(`${API_BASE_URL}/api/payment/user/package/create-qr`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify(params)
  })
  
  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.message || 'Failed to create package purchase QR')
  }
  
  return response.json()
}

/**
 * Get package purchase transaction status
 */
export async function getPackagePurchaseStatus(
  transactionId: string
): Promise<PackagePurchaseStatusResponse> {
  const token = localStorage.getItem('authToken')
  
  const response = await fetch(
    `${API_BASE_URL}/api/payment/user/status/${transactionId}`,
    {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    }
  )
  
  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.message || 'Failed to get transaction status')
  }
  
  return response.json()
}

/**
 * Get package purchase transaction history
 */
export async function getPackagePurchaseHistory(
  limit: number = 10,
  offset: number = 0
): Promise<PackagePurchaseHistory> {
  const token = localStorage.getItem('authToken')
  
  const response = await fetch(
    `${API_BASE_URL}/api/payment/user/transactions?type=package_purchase&limit=${limit}&offset=${offset}`,
    {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    }
  )
  
  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.message || 'Failed to get transaction history')
  }
  
  return response.json()
}

/**
 * Get all available packages with current package info
 */
export async function getAvailablePackages(): Promise<{
  currentPackage: any
  availablePackages: any[]
  paymentInfo: any
  pendingPackage?: any
}> {
  const token = localStorage.getItem('authToken')
  
  const response = await fetch(
    `${API_BASE_URL}/api/customer/packages`,
    {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    }
  )
  
  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.message || 'Failed to get available packages')
  }
  
  const result = await response.json()
  console.log('📦 Available packages API response:', result)
  console.log('📦 PendingPackage specifically:', result.data?.pendingPackage)
  return result.data
}

/**
 * DEPRECATED: Use getAvailablePackages() instead
 * This endpoint does not exist in backend
 */
/*
export async function getCustomerPackageStatus(): Promise<CustomerPackageStatus> {
  const token = localStorage.getItem('authToken')
  
  const response = await fetch(
    `${API_BASE_URL}/api/customers/package-status`,
    {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    }
  )
  
  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.message || 'Failed to get package status')
  }
  
  return response.json()
}
*/

/**
 * Compare package for downgrade/upgrade detection
 */
export async function comparePackages(
  currentPackageId: string,
  newPackageId: string
): Promise<PackageComparison> {
  const token = localStorage.getItem('authToken')
  
  const response = await fetch(
    `${API_BASE_URL}/api/packages/compare?current=${currentPackageId}&new=${newPackageId}`,
    {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    }
  )
  
  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.message || 'Failed to compare packages')
  }
  
  return response.json()
}

// ============================================================================
// React Query Hooks
// ============================================================================

/**
 * Hook to create package purchase QR code
 * 
 * @example
 * ```tsx
 * const { mutate: purchasePackage, isPending } = useCreatePackagePurchaseQR()
 * 
 * purchasePackage('package-uuid', {
 *   onSuccess: (data) => {
 *     console.log('QR created:', data.data.qrCode)
 *   },
 *   onError: (error) => {
 *     console.error('Failed:', error.message)
 *   }
 * })
 * ```
 */
export function useCreatePackagePurchaseQR() {
  return useMutation({
    mutationFn: (params: PackagePurchaseQRRequest) => createPackagePurchaseQR(params),
    onError: (error: Error) => {
      console.error('Package purchase QR creation failed:', error)
    }
  })
}

/**
 * Hook to check package purchase transaction status
 * Automatically polls every 3 seconds while pending
 * 
 * @example
 * ```tsx
 * const { data, isLoading } = useCheckPackagePurchaseStatus(transactionId)
 * 
 * if (data?.data.isCompleted) {
 *   // Payment completed
 * }
 * ```
 */
export function useCheckPackagePurchaseStatus(transactionId: string | null) {
  return useQuery({
    queryKey: ['package-purchase-status', transactionId],
    queryFn: () => {
      if (!transactionId) throw new Error('No transaction ID')
      return getPackagePurchaseStatus(transactionId)
    },
    enabled: !!transactionId,
    refetchInterval: (query) => {
      const data = query?.state?.data
      
      // Stop polling if completed or failed
      if (data?.data?.isCompleted || data?.data?.isFailed) {
        return false
      }
      
      // Continue polling every 3 seconds while pending
      return 3000
    },
    refetchIntervalInBackground: true,
    refetchOnWindowFocus: true
  })
}

/**
 * Hook to get package purchase history
 * 
 * @example
 * ```tsx
 * const { data, isLoading } = usePackagePurchaseHistory(10, 0)
 * 
 * data?.transactions.map(tx => (
 *   <div key={tx.id}>{tx.package_name}</div>
 * ))
 * ```
 */
export function usePackagePurchaseHistory(
  limit: number = 10,
  offset: number = 0
) {
  return useQuery({
    queryKey: ['package-purchase-history', limit, offset],
    queryFn: () => getPackagePurchaseHistory(limit, offset),
    staleTime: 30000 // 30 seconds
  })
}

/**
 * Hook to get all available packages
 * 
 * @example
 * ```tsx
 * const { data: packagesData, isLoading } = useAvailablePackages()
 * 
 * packagesData?.availablePackages.map(pkg => (
 *   <div key={pkg.id}>{pkg.name}</div>
 * ))
 * ```
 */
export function useAvailablePackages() {
  return useQuery({
    queryKey: ['available-packages'],
    queryFn: getAvailablePackages,
    staleTime: 60000, // 1 minute
    refetchOnWindowFocus: false
  })
}

/**
 * DEPRECATED: Use useAvailablePackages() instead
 * 
 * @example
 * ```tsx
 * const { data: packagesData } = useAvailablePackages()
 * const currentPackage = packagesData?.currentPackage
 * const pendingPackage = packagesData?.pendingPackage
 * ```
 */
/*
export function useCustomerPackageStatus() {
  return useQuery({
    queryKey: ['customer-package-status'],
    queryFn: getCustomerPackageStatus,
    staleTime: 10000, // 10 seconds
    refetchOnWindowFocus: true
  })
}
*/

/**
 * Hook to compare packages for downgrade detection
 * 
 * @example
 * ```tsx
 * const { data: comparison, isLoading } = useComparePackages(
 *   currentPackageId,
 *   newPackageId
 * )
 * 
 * if (comparison?.isDowngrade) {
 *   // Show downgrade warning
 * }
 * ```
 */
export function useComparePackages(
  currentPackageId: string | null,
  newPackageId: string | null
) {
  return useQuery({
    queryKey: ['compare-packages', currentPackageId, newPackageId],
    queryFn: () => {
      if (!currentPackageId || !newPackageId) {
        throw new Error('Missing package IDs')
      }
      return comparePackages(currentPackageId, newPackageId)
    },
    enabled: !!currentPackageId && !!newPackageId,
    staleTime: 60000 // 1 minute
  })
}

// ============================================================================
// Utility Hooks
// ============================================================================

/**
 * Hook that combines package status and comparison
 * Useful for package selection UI
 * 
 * @example
 * ```tsx
 * const packageInfo = usePackageSelectionInfo(selectedPackageId)
 * 
 * if (packageInfo.canPurchase && packageInfo.isDowngrade) {
 *   // Show warning but allow purchase
 * }
 * ```
 */
export function usePackageSelectionInfo(selectedPackageId: string | null) {
  const { data: packagesData } = useAvailablePackages()
  const { data: comparison } = useComparePackages(
    packagesData?.currentPackage?.id ?? null,
    selectedPackageId
  )
  
  return {
    canPurchase: !packagesData?.pendingPackage, // Can purchase if no pending package
    hasPendingPackage: !!packagesData?.pendingPackage,
    isCurrentPackage: packagesData?.currentPackage?.id === selectedPackageId,
    isDowngrade: comparison?.isDowngrade ?? false,
    isUpgrade: comparison?.isUpgrade ?? false,
    lostFeatures: comparison?.lostFeatures ?? [],
    gainedFeatures: comparison?.gainedFeatures ?? []
  }
}

/**
 * Hook for managing QR code countdown
 * 
 * @example
 * ```tsx
 * const { timeRemaining, isExpired, formatTime } = useQRCountdown(expiresAt)
 * 
 * <div>Time: {formatTime()}</div>
 * ```
 */
export function useQRCountdown(expiresAt: string | null) {
  const [timeRemaining, setTimeRemaining] = React.useState<number>(180) // 3 minutes
  
  React.useEffect(() => {
    if (!expiresAt) return
    
    const calculateTimeRemaining = () => {
      const now = new Date().getTime()
      const expiry = new Date(expiresAt).getTime()
      const diff = Math.max(0, Math.floor((expiry - now) / 1000))
      setTimeRemaining(diff)
    }
    
    calculateTimeRemaining()
    const interval = setInterval(calculateTimeRemaining, 1000)
    
    return () => clearInterval(interval)
  }, [expiresAt])
  
  const formatTime = () => {
    const minutes = Math.floor(timeRemaining / 60)
    const seconds = timeRemaining % 60
    return `${minutes}:${seconds.toString().padStart(2, '0')}`
  }
  
  return {
    timeRemaining,
    isExpired: timeRemaining === 0,
    formatTime
  }
}
